import { clientInfo } from '@meitu/pc-bridge';
import { createFontStyle, getFramework } from '@/framework';
import { isSafari } from '@/utils/browser-kit';
import dom2Image from '@/utils/dom-to-image';
import { DEFAULT_FONT_FAMILY, ITableLayer } from '@/version-types';
import { getTdElementId } from '../view/cell-component';
import { SAAS_EDITOR_TABLE_CLASS_NAME } from '../view/table-layer';
import renderTempTableLayer from './render-temp-table-layer';
import { collectTableLayerFonts, getCellStyle } from './table';

/** 返回的图片尺寸与页面上展示的宽高相同(即缩放之后) */
export const table2Canvas = async (
    originalLayer: ITableLayer,
    frameworkIdentity: string,
): Promise<HTMLCanvasElement> => {
    console.time('===table2Canvas');
    let canvas: HTMLCanvasElement | null = null;

    const framework = getFramework(frameworkIdentity);

    await renderTempTableLayer({
        stageScale: framework.stage.getScale(),
        layer: originalLayer,
        task: async (containerEl, containerElId) => {
            const el = containerEl.querySelector<HTMLDivElement>(`.${SAAS_EDITOR_TABLE_CLASS_NAME}`)!;

            const extraCssStrings: string[] = [];

            if (isSafari || clientInfo.isClient) {
                // 防止第一行是合并单元格的情况下, 表格布局错乱 (issue: https://jira.meitu.com/browse/MTXXPRO-33792)
                // 这里强行加入css覆盖computedStyle生成的width: 0px; (safari下就是0px，windows内嵌是qt浏览器, 也是一样)
                let colWidthsCssText = '';
                el.querySelectorAll('col').forEach((colEl, colIndex) => {
                    const cssWidth = colEl.style.width;
                    if (cssWidth) {
                        colWidthsCssText += `\
                            #${containerElId} col:nth-child(${colIndex + 1}) {\
                                width: ${cssWidth} !important;
                            }\
                        `;
                    }
                });
                extraCssStrings.push(colWidthsCssText);
            }

            const createTextSubSet = async (): Promise<{ fontFamilyMap: Record<string, string>; hasFont: boolean }> => {
                const layerIsInStage = !!framework.getLayer(originalLayer.id);
                if (layerIsInStage) {
                    // 返回的是带文字的图层, 如果没文字，就不会返回
                    const layerWithFontFamilyMap = ((
                        await framework.createFontFamilySubset([originalLayer], true)
                    )[0] ?? null) as ITableLayer | null;
                    const hasFont = !!layerWithFontFamilyMap;
                    const fontFamilyMap = layerWithFontFamilyMap?.__runtime?.fontFamilyMap ?? {};
                    return { fontFamilyMap, hasFont };
                }

                const fonts = collectTableLayerFonts(originalLayer.data);
                const createTextSubsetPromises = fonts.map((font) =>
                    framework
                        .createTextSubset(font.text, font.fontFamily)
                        .then((subsetName) => (subsetName ? { subsetName, fontFamily: font.fontFamily } : null)),
                );
                const createTextSubsetResults = (await Promise.all(createTextSubsetPromises)).filter(Boolean) as {
                    subsetName: string;
                    fontFamily: string;
                }[];
                const fontFamilyMap = createTextSubsetResults.reduce<Record<string, string>>((acc, item) => {
                    acc[item.fontFamily] = item.subsetName;
                    return acc;
                }, {});
                const hasFont = createTextSubsetResults.length > 0;
                return { fontFamilyMap, hasFont };
            };

            // 1. 加载字体子集
            const { fontFamilyMap, hasFont } = await createTextSubSet();
            if (hasFont) {
                const fontFamily2TdElIdMap: Record<string, string[]> = {};
                originalLayer.data.cells.flat().forEach((cell) => {
                    if (!cell) return;
                    if (!cell.content) return;

                    // 2. 把每个单元格的font-family: fontFamilySubset !important加上
                    const tdElementId = getTdElementId({
                        id: cell.__renderId,
                        hIndex: cell.hIndex,
                        vIndex: cell.vIndex,
                    });
                    const style = getCellStyle(cell, originalLayer.data);
                    const fontFamily = style?.fontFamily ?? DEFAULT_FONT_FAMILY().name;
                    const fontFamilySubset = fontFamilyMap?.[fontFamily];
                    if (!fontFamilySubset) {
                        throw new Error('tableLayer fontFamilySubset not found');
                    }
                    if (!fontFamily2TdElIdMap[fontFamilySubset]) {
                        fontFamily2TdElIdMap[fontFamilySubset] = [];
                    }
                    if (!fontFamily2TdElIdMap[fontFamilySubset].includes(tdElementId)) {
                        fontFamily2TdElIdMap[fontFamilySubset].push(tdElementId);
                    }
                    // 3. 字体子集css都加上
                    const base64 = framework.getSubsetBase64(fontFamilySubset);
                    if (!base64) {
                        console.log('===tableLayer fontFamilySubset base64 not found', {
                            content: cell.content,
                            fontFamilySubset,
                            base64,
                        });
                        throw new Error('tableLayer fontFamilySubset base64 not found');
                    }
                    const fontFamilyStyle = createFontStyle(fontFamilySubset, base64, true);
                    if (!extraCssStrings.includes(fontFamilyStyle)) {
                        extraCssStrings.push(fontFamilyStyle);
                    }
                });

                Object.entries(fontFamily2TdElIdMap).forEach(([fontFamilySubset, tdElIds]) => {
                    extraCssStrings.push(`#${tdElIds.join(',')} { font-family: ${fontFamilySubset} !important; }`);
                });
            }

            console.time('===dom2Image.toCanvas');
            canvas = await dom2Image.toCanvas(el, {
                extraCssStrings,
                disableWebFonts: true,
            });
            console.timeEnd('===dom2Image.toCanvas');
        },
    });

    console.timeEnd('===table2Canvas');

    return canvas!;
};
